<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
    show: {
        type: Boolean,
        default: true,
    },
    maxWidth: {
        type: String,
        default: "2xl",
    },
    closeable: {
        type: Boolean,
        default: true,
    },
    blur: {
        type: Boolean,
        default: true,
    },
    hideCloseButton: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["close"]);
const showSlot = ref(props.show);

watch(
    () => props.show,
    () => {
        if (typeof document === "undefined") return; // Evita rodar no SSR

        if (props.show) {
            document.body.style.overflow = "hidden";
            showSlot.value = true;
        } else {
            document.body.style.overflow = null;
            setTimeout(() => {
                showSlot.value = false;
            }, 300);
        }
    }
);

const close = () => {
    if (props.closeable) {
        emit("close");
    }
};

const closeOnEscape = (e) => {
    if (e.key === "Escape") {
        e.preventDefault();

        if (props.show) {
            close();
        }
    }
};

onMounted(() => document.addEventListener("keydown", closeOnEscape));

onUnmounted(() => {
    document.removeEventListener("keydown", closeOnEscape);
    document.body.style.overflow = null;
});

const maxWidthClass = computed(() => {
    return {
        sm: "sm:max-w-sm",
        md: "sm:max-w-md",
        lg: "sm:max-w-lg",
        xl: "sm:max-w-xl",
        "2xl": "sm:max-w-2xl",
        "3xl": "sm:max-w-3xl",
        "4xl": "sm:max-w-4xl",
        "5xl": "sm:max-w-5xl",
        "6xl": "sm:max-w-6xl",
        "7xl": "sm:max-w-7xl",
        screen: "sm:max-w-[min(90rem,92vw)]",
    }[props.maxWidth];
});
</script>

<template>
    <Teleport to="body">
        <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
            <div
                class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8 z-50 flex items-center justify-center"
                scroll-region
            >
                <!-- Enhanced backdrop with blur -->
                <transition
                    enter-active-class="ease-out duration-500"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-300"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="show"
                        class="fixed inset-0 transform transition-all"
                        :class="blur ? 'backdrop-blur-sm' : ''"
                        @click="close"
                    >
                        <div class="absolute inset-0 bg-black/50" />
                    </div>
                </transition>

                <!-- Enhanced modal with improved animations -->
                <transition
                    enter-active-class="ease-out duration-500"
                    enter-from-class="opacity-0 scale-95 translate-y-8"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-active-class="ease-in duration-300"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 translate-y-8"
                >
                    <div
                        v-show="show"
                        v-motion
                        :initial="{ opacity: 0, scale: 0.9, y: 20 }"
                        :enter="{ opacity: 1, scale: 1, y: 0 }"
                        :leave="{ opacity: 0, scale: 0.9, y: 20 }"
                        :transition="{
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                        }"
                        class="relative w-full bg-white shadow-2xl transform transition-all sm:w-full sm:mx-auto max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-4rem)] overflow-y-auto"
                        :class="[
                            maxWidthClass,
                            'rounded-3xl ',
                            'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]',
                        ]"
                    >
                        <!-- Close button -->
                        <button
                            v-if="closeable && !hideCloseButton"
                            @click="close"
                            class="absolute top-4 right-4 z-10 p-2 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-gray-200"
                        >
                            <svg
                                class="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        <!-- Content with padding and smooth animations -->
                        <div
                            v-if="showSlot"
                            v-motion
                            :initial="{ opacity: 0, y: 10 }"
                            :enter="{ opacity: 1, y: 0 }"
                            :transition="{ duration: 400, delay: 100 }"
                            class="relative"
                        >
                            <slot />

                            <div v-if="$slots.bottom" class="p-4">
                                <slot name="bottom" />
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
/* Custom scrollbar for modal content */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: rgba(243, 244, 246, 0.5);
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.5);
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.7);
}

/* Enhanced backdrop blur effect */
@supports (backdrop-filter: blur(8px)) {
    .backdrop-blur-sm {
        backdrop-filter: blur(8px);
    }
}

/* Smooth modal entrance with subtle scale */
@keyframes modalEnter {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}
</style>
